import { useCallback, useRef, useState } from "react";
import Model from "@tripian/model";
import { api } from "@tripian/core";

export interface UseBookableToursParams {
  poiId?: string;
  cityId?: string | number;
  /** POI'nin planda yer aldığı gün (YYYY-MM-DD) */
  date?: string;
  adults?: number;
  currency?: string;
  lang?: string;
  limit?: number;
}

/**
 * Odaklanan POI için bookable ürünleri POST /tour-api/search ile çeker (POI modu).
 *
 * poiId verildiğinde backend yalnızca o POI'ye bağlı ürünlere iner ve
 * instantAvailability=1 ile müsaitlik zenginleştirmesini aynı istekte yapar —
 * bu yüzden ayrı bir schedule/availability turuna gerek yoktur.
 *
 * Sayfalama offset tabanlı: loadMore her çağrıldığında bir sonraki sayfa mevcut
 * listeye eklenir. Dönen sayfa limit'ten küçükse elde başka ürün yok demektir.
 */
const useBookableTours = (params: UseBookableToursParams) => {
  const {
    poiId,
    cityId,
    date,
    adults = 1,
    currency = "usd",
    lang = "en",
    limit = 10,
  } = params;

  const [products, setProducts] = useState<Model.TourSearchProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();

  const offsetRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  // Hangi POI+tarih için ilk sayfa zaten yüklendi. Tüketicideki effect, focus objesi
  // yenilendiğinde tekrar çalışabiliyor; bu olmadan liste birinci sayfaya geri dönerdi.
  const loadedKeyRef = useRef<string | null>(null);

  const fetchPage = useCallback(
    async (offset: number) => {
      if (!poiId || !date) return;
      // Sentinel görünürlüğü ve effect'ler aynı sayfayı iki kez isteyebilir
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isFirstPage = offset === 0;
      if (isFirstPage) setLoading(true);
      else setLoadingMore(true);
      setError(undefined);

      try {
        const request: Partial<Model.TourSearchRequest> = {
          instantAvailability: 1,
          poiId,
          ...(cityId !== undefined ? { cityId } : {}),
          adults,
          currency,
          date,
          lang,
          offset,
          limit,
        };

        const result = await api.tourSearch(request as Model.TourSearchRequest);
        const batch = result.products || [];

        setProducts(prev => (isFirstPage ? batch : [...prev, ...batch]));
        // Tam sayfa geldiyse arkada daha ürün olabilir; eksik geldiyse liste bitti.
        setHasMore(batch.length === limit);
        offsetRef.current = offset + batch.length;
      } catch (err: any) {
        console.error("Bookable tour search Error:", err);
        if (isFirstPage) setProducts([]);
        setHasMore(false);
        setError(err?.message || "Tour search failed");
      }

      inFlightRef.current = false;
      if (isFirstPage) setLoading(false);
      else setLoadingMore(false);
    },
    [poiId, cityId, date, adults, currency, lang, limit]
  );

  /** İlk sayfayı çeker. Aynı POI+tarih için ikinci çağrı yok sayılır (idempotent). */
  const fetchProducts = useCallback(() => {
    if (!poiId || !date) return undefined;
    const key = `${poiId}|${date}`;
    if (loadedKeyRef.current === key) return undefined;
    loadedKeyRef.current = key;
    offsetRef.current = 0;
    return fetchPage(0);
  }, [fetchPage, poiId, date]);

  const loadMore = useCallback(() => {
    if (!hasMore || inFlightRef.current) return undefined;
    return fetchPage(offsetRef.current);
  }, [fetchPage, hasMore]);

  const reset = useCallback(() => {
    offsetRef.current = 0;
    inFlightRef.current = false;
    loadedKeyRef.current = null;
    setProducts([]);
    setHasMore(false);
    setLoading(false);
    setLoadingMore(false);
    setError(undefined);
  }, []);

  return {
    products,
    loading,
    loadingMore,
    hasMore,
    error,
    fetchProducts,
    loadMore,
    reset,
  };
};

export default useBookableTours;
